import * as React from "react";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { useEffect, useMemo, useState } from "react";
import { TableComponents, TableVirtuoso } from "react-virtuoso";
import { Box, Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Tooltip } from "@mui/material";
import { IReportGenerated2 } from "source/models/report/reportGenerated2";
import { ITableRow, IValidationErrorDictionary, ReportExcelReportTableRowData } from "./ReportExcelReportTableRowData";
import { visuallyHidden } from "@mui/utils";

export interface IReportExcelReportTablesProp {
    reportData: IReportGenerated2;
    sortingDisabled?: boolean;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    multiSelect?: boolean;
    selectedId?: string;
    selectedColumn?: string;
    checkedIds?: string[];
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onSetSelectedIds?: (selectedId: string, checkedIds: string[]) => void;
    onColumnClick?: (column: string) => void;
}

export interface IReportExcelReportTablesState {
    sortColumn: string;
    sortOrderIsAsc: boolean;
}

export const ReportExcelReportTables = (props: IReportExcelReportTablesProp) => {
    const hasMultipleTables = props.reportData.tables.length > 1;

    const getValidSortColumn = (taskType: ITaskEditTypeItem, sortColumn: string): string => {
        let result = sortColumn ?? "";
        if (taskType && taskType.lineCols.length > 0) {
            if (!taskType.lineCols.find(i => i.field === result)) {
                result = taskType.lineCols[0].field;
            }
        }
        return result;
    };

    const [state, setState] = useState<IReportExcelReportTablesState>({
        sortColumn: "",
        sortOrderIsAsc: true
    });

    useEffect(() => {
        setState({
            sortColumn: hasMultipleTables || !props.reportData.tables?.length
                ? ""
                : getValidSortColumn(props.reportData.tables[0].taskType, props.sortColumn),
            sortOrderIsAsc: !hasMultipleTables && props.sortColumn
                ? props.sortOrderIsAsc
                : true
        });
    }, []);
      
    const tableRows = useMemo(() => {
        const result: ITableRow[] = [];
        for (let index = 0; index < props.reportData.tables.length; index++) {
            const table = props.reportData.tables[index];
            //Add header row to data
            if (hasMultipleTables) {
                result.push({
                    taskType: table.taskType,
                    dataRow: null,
                });
            }
            //Add data rows to data
            const dataRows = table.dataRows ? table.dataRows.slice(0) : [];
            TaskDataRow.sortTaskDataRowsByColumn(dataRows, table.taskType, state.sortColumn, state.sortOrderIsAsc);
            for (const dataRow of dataRows) {
                const dataRowErrors: IValidationErrorDictionary = {};
                const dataRowInvisibleColErrors: IValidationErrorDictionary = {};
                const validationRow = table.validationRows?.length && dataRow
                    ? table.validationRows.find(i => i.id === dataRow.id)
                    : null;
                if (validationRow) {
                    for (const taskTypeCol of table.taskType.formCols) {
                        const validationCol = validationRow
                            ? validationRow.cols.find(j => j.field === taskTypeCol.field)
                            : null;
                        if (!validationCol || validationCol.errors.length === 0) continue;
                        dataRowErrors[taskTypeCol.field] = validationCol.errors;
                        const classes = taskTypeCol.class ? taskTypeCol.class.split(" ") : [];
                        const lineCol = table.taskType.lineCols.find(i => i.field === taskTypeCol.field);
                        if (lineCol && !classes.includes("d-none-web")) continue;
                        dataRowInvisibleColErrors[taskTypeCol.field] = validationCol.errors;
                    }
                }
                result.push({
                    taskType: table.taskType,
                    dataRow,
                    dataRowErrors,
                    dataRowInvisibleColErrors
                });
            }
            //Add divider row to data
            if (index < props.reportData.tables.length - 1) {
                result.push({
                    taskType: table.taskType,
                    dataRow: null,
                    divider: true,
                });
            }
        }
        return result;
    }, [props.reportData, state.sortColumn, state.sortOrderIsAsc]);

    const VirtuosoTableComponents: TableComponents<ITableRow> = {
        // eslint-disable-next-line react/display-name
        Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
            <TableContainer component={Paper} {...props} ref={ref} />
        )),
        Table: (props) => (
            <Table {...props} sx={{ borderCollapse: "separate", tableLayout: "fixed" }} size="small" />
        ),
        // eslint-disable-next-line react/display-name
        TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
            <TableHead {...props} ref={ref} />
        )),
        TableRow: (rowProps) => {
            const rowHasInvisibleColErrors = Object.keys(rowProps.item.dataRowInvisibleColErrors || {}).length > 0;
            const invisibleColErrors = (rowHasInvisibleColErrors && Object.keys(rowProps.item.dataRowInvisibleColErrors).map(i => rowProps.item.dataRowInvisibleColErrors[i]).join(", ")) ?? "";
            const tableRow = <TableRow
                {...rowProps}
                hover={!rowHasInvisibleColErrors}
                selected={rowProps.item.dataRow?.id && rowProps.item.dataRow?.id === props.selectedId}
                onClick={props.onSetSelectedIds ? () => props.onSetSelectedIds(rowProps.item.dataRow?.id, props.checkedIds) : null}
                sx={{
                    backgroundColor: rowHasInvisibleColErrors ? "error.main" : null
                }}
            />;
            if (rowHasInvisibleColErrors) {
                return <Tooltip title={invisibleColErrors} arrow placement="top-start">
                    {tableRow}
                </Tooltip>;
            }
            return tableRow;
        },
        // eslint-disable-next-line react/display-name
        TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
            <TableBody {...props} ref={ref} />
        )),
    };

    const handleChangeSort = (sortColumn: string, sortOrderIsAsc: boolean) => {
        setState(old => ({
            ...old,
            sortColumn,
            sortOrderIsAsc
        }));
        if (props.onSetSortData) {
            props.onSetSortData(state.sortColumn, state.sortOrderIsAsc);
        }
    };

    const allChecked = !hasMultipleTables && props.reportData.tables?.length > 0 && tableRows.length > 0 && props.checkedIds.length === tableRows.length;

    const handleToggleChecked = () => {
        let checkedIds: string[] = [];
        const checkableIds: string[] = [];
        for (const tableRow of tableRows) {
            if (!tableRow.dataRow) continue;
            if (Object.keys(tableRow.dataRowErrors || {}).length > 0) {
                continue;
            }
            checkableIds.push(tableRow.dataRow?.id);
        }
        if (checkableIds.length !== props.checkedIds.length) {
            checkedIds = checkableIds;
        }
        if (!props.onSetSelectedIds) return;
        props.onSetSelectedIds(props.selectedId, checkedIds);
    };

    const checkBox = <Checkbox
        color="primary"
        indeterminate={props.checkedIds?.length > 0 && props.checkedIds.length < tableRows.length}
        checked={allChecked}
        sx={{ marginLeft: "-5px", marginRight: "5px", padding: "0" }}
        onChange={handleToggleChecked}
        onClick={(e) => e.stopPropagation()}
    />;

    const fixedHeaderContent = hasMultipleTables || !props.reportData.tables?.length
        ? null
        : () => (<TableRow sx={{ background: "white" }}>
            {props.reportData.tables[0].taskType.lineCols.map((i, index) =>
                <TableCell
                    key={i.field}
                    className={i.class + (!!props.selectedColumn && props.selectedColumn === i.field ? " col-selected" : "")}
                    sx={{ textOverflow: "clip", overflow: "hidden", whiteSpace: "nowrap", backgroundColor: "action.hover" }}
                >
                    {(!props.sortingDisabled
                        ? <TableSortLabel
                            active={state.sortColumn === i.field}
                            direction={state.sortOrderIsAsc ? "asc" : "desc"}
                            onClick={() => handleChangeSort(i.field, state.sortColumn === i.field ? !state.sortOrderIsAsc : true)}
                        >
                            {(props.multiSelect && index === 0 ? checkBox : null)}
                            {i.title}
                            {state.sortColumn === i.field &&
                                <Box component="span" sx={visuallyHidden}>{state.sortOrderIsAsc ? "sorted ascending" : "sorted descending"}</Box>
                            }
                        </TableSortLabel>
                        : <>
                            {(props.multiSelect && index === 0 ? checkBox : null)}
                            {i.title}
                        </>
                    )}
                </TableCell>
            )}
        </TableRow>);

    return (
        <TableVirtuoso
            style={{ height: "100%" }}
            data={tableRows}
            components={VirtuosoTableComponents}
            fixedHeaderContent={fixedHeaderContent}
            itemContent={(index, tableRow) => <ReportExcelReportTableRowData
                tableRow={tableRow}
                multiSelect={props.multiSelect}
                checkedIds={props.checkedIds}
                onSetCheckedIds={props.onSetSelectedIds ? (checkedIds) => props.onSetSelectedIds(props.selectedId, checkedIds) : null}
            />}
        />
    );
};